<template>
  <div
    class="select-input select-input--autocomplete"
    :class="{
      'select-input--open': showValues && values.length > 0,
      'select-input--error': errorMessage,
      'select-input--loading': loading
    }"
  >
    <text-input
      type="text"
      :autocomplete="autocomplete"
      :hide-error="true"
      :label="label"
      :name="name"
      :placeholder="placeholder"
      :model-value="modelValue"
      :validations="validations"
      @blur="blurHandler"
      @focus="focusHandler"
      @input="inputHandler"
      @keydown.up.prevent="keyUpHandler"
      @keydown.down.prevent="keyDownHandler"
      @keydown.enter.prevent="enterKeyHandler"
      @keydown.esc="escapeKeyHandler"
    />

    <ul
      v-show="showValues && values.length > 0"
      ref="selectMenu"
      class="select-input__values"
    >
      <li
        v-for="(v, index) in values"
        :id="`${name}-value-${index}`"
        :key="`${name}-value-${index}`"
        ref="selectValue"
        class="select-input__value"
        :class="{
          'select-input__value--focused': index === focusedValueIndex
        }"
        @mousedown="selectValue(v)"
      >
        {{ v.display }}
      </li>
    </ul>

    <input-error :error="errorMessage" />
  </div>
</template>



<script>
  import { useFieldError } from 'vee-validate';

  export default {
    name: 'AutocompleteInput',

    inject: {
      validatedForm: {
        from: 'validatedForm'
      }
    },

    props: {
      autocomplete: {
        type: String,
        default: null
      },

      label: {
        type: String,
        required: true
      },

      loading: {
        type: Boolean,
        default: false
      },

      name: {
        type: String,
        required: true
      },

      placeholder: {
        type: String,
        default: null
      },

      validations: {
        type: Object,
        default() {
          return {};
        }
      },

      modelValue: {
        type: String,
        default: null
      },

      values: {
        type: Array,
        default() {
          return [];
        }
      }
    },

    setup(props) {
      const errorMessage = useFieldError(() => props.name);

      return { errorMessage };
    },

    data() {
      return {
        focusedValueIndex: 0,
        showValues: false
      };
    },

    watch: {
      focusedValueIndex: 'focusedValueIndexChangeHandler'
    },

    methods: {
      blurHandler() {
        this.updateShowValues(false);
      },

      enterKeyHandler(event) {
        const value = this.values[this.focusedValueIndex];

        this.selectValue(value);

        event.target.blur();
      },

      escapeKeyHandler(event) {
        event.target.blur();
      },

      focusHandler() {
        this.updateShowValues(true);
      },

      focusedValueIndexChangeHandler(index) {
        if (!this.$refs.selectValue) {
          return;
        }

        const focusedValue = this.$refs.selectValue[index];

        if (focusedValue) {
          this.$nextTick(() => {
            focusedValue.scrollIntoView({
              behavior: 'auto',
              block: 'nearest',
              inline: 'start'
            });
          });
        }
      },

      inputHandler(value) {
        this.$emit('update:modelValue', value);
        this.$emit('search-term-input', value);

        this.focusedValueIndex = 0;
      },

      keyDownHandler() {
        if (!this.showValues) {
          this.updateShowValues(true);
        }

        else if (this.values.length - 1 > this.focusedValueIndex) {
          this.focusedValueIndex++;
        }
      },

      keyUpHandler() {
        if (this.focusedValueIndex > 0) {
          this.focusedValueIndex--;
        }
      },

      selectValue(value) {
        this.updateShowValues(false);

        if (value) {
          this.$emit('autocompleted', value);
        }
      },

      updateShowValues(showValues) {
        this.showValues = showValues;

        if (showValues) {
          const matchingValueIndex = this.values.findIndex(value => value.display === this.modelValue);

          if (matchingValueIndex > -1) {
            this.focusedValueIndex = matchingValueIndex;
          }

          else {
            this.focusedValueIndex = 0;
          }

          this.focusedValueIndexChangeHandler(this.focusedValueIndex);
        }
      }
    }
  };
</script>
